import { useState, type ChangeEvent } from "react";
import { importHosts, type HostImportResult } from "./api";
import { useSubscription } from "./useSubscription";
import { LockedFeature } from "./LockedFeature";
import { useToast } from "../../components/useToast";

function downloadCsvTemplate() {
  const csv = "name,email,phone,department\nJane Doe,jane.doe@example.com,9876543210,Engineering\n";
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "host-import-template.csv";
  anchor.click();
  URL.revokeObjectURL(url);
}

export function ImportHostsCard({ onImported }: { onImported: () => void }) {
  const { showSuccess, showError } = useToast();
  const { data: subscription, loading: subscriptionLoading } = useSubscription();
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<HostImportResult | null>(null);

  async function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setPending(true);
    setResult(null);
    try {
      const importResult = await importHosts(file);
      setResult(importResult);
      showSuccess(`Imported ${importResult.created} of ${importResult.total_rows} rows`);
      if (importResult.created > 0) onImported();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Import failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <LockedFeature
      feature="host_import"
      featureLabel="Bulk host import"
      subscription={subscription}
      loading={subscriptionLoading}
    >
      <article className="action-form action-card">
        <label htmlFor="host-import-file">Import Hosts (CSV or XLSX)</label>
        <div className="action-row">
          <input
            id="host-import-file"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(event) => void handleFileSelected(event)}
            disabled={pending}
          />
          <button className="refresh-btn" type="button" onClick={downloadCsvTemplate}>
            Download template
          </button>
        </div>
        <p className="field-hint">
          Columns: name (required), email, phone, department -- first row must be the header.
        </p>
        {pending ? <p>Importing...</p> : null}
        {result ? (
          <div className="import-result">
            <p className={result.skipped.length > 0 ? "field-hint" : "success-banner"}>
              Created {result.created} of {result.total_rows} row{result.total_rows === 1 ? "" : "s"}.
            </p>
            {result.skipped.length > 0 ? (
              <ul className="import-skipped-list">
                {result.skipped.map((row) => (
                  <li key={row.row}>
                    Row {row.row}
                    {row.name ? ` (${row.name})` : ""}: {row.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </article>
    </LockedFeature>
  );
}
